import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';

export default function EarningsCalculator() {
  const { language } = useLanguage();
  const t = CONTENT[language].calculator;

  const [patients, setPatients] = useState(100);
  const [poolAmount, setPoolAmount] = useState(100000);
  const [surplusPercent, setSurplusPercent] = useState(60);
  const [providerSharePercent, setProviderSharePercent] = useState(20);
  const [showExplanation, setShowExplanation] = useState(false);

  const surplus = poolAmount * (surplusPercent / 100);
  const providerShare = surplus * (providerSharePercent / 100);

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'es-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="bg-suncoast-offWhite p-6 border-b border-gray-100">
        <h3 className="text-xl font-bold text-suncoast-dark">{t.title}</h3>
      </div>
      
      <div className="p-6 md:p-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Inputs */}
          <div className="space-y-6">
            <div>
              <label className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                {t.labels.patients}
                <span className="text-suncoast-primary">{patients}</span>
              </label>
              <input 
                type="range" min="50" max="500" step="10" 
                value={patients} onChange={(e) => setPatients(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-suncoast-primary"
              />
            </div>
            <div>
              <label className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                {t.labels.pool}
                <span className="text-suncoast-primary">{formatCurrency(poolAmount)}</span>
              </label>
              <input 
                type="range" min="50000" max="500000" step="5000" 
                value={poolAmount} onChange={(e) => setPoolAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-suncoast-primary"
              />
            </div>
            <div>
               <label className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                {t.labels.surplus}
                <span className="text-suncoast-primary">{surplusPercent}%</span>
              </label>
              <input 
                type="range" min="0" max="100" step="5" 
                value={surplusPercent} onChange={(e) => setSurplusPercent(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-suncoast-primary"
              />
            </div>
            <div>
               <label className="flex justify-between text-sm font-bold text-gray-700 mb-2">
                {t.labels.share}
                <span className="text-suncoast-primary">{providerSharePercent}%</span>
              </label>
              <input 
                type="range" min="10" max="50" step="5" 
                value={providerSharePercent} onChange={(e) => setProviderSharePercent(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-suncoast-primary"
              />
            </div>
          </div>

          {/* Visualization */}
          <div className="flex flex-col justify-center">
             <div className="space-y-6">
                <div className="bg-gray-100 p-4 rounded-lg">
                   <div className="flex justify-between text-sm mb-1 text-gray-500">
                     <span>{t.labels.pool}</span>
                     <span>{formatCurrency(poolAmount)}</span>
                   </div>
                   <div className="w-full bg-gray-300 h-4 rounded-full"></div>
                </div>

                <div className="bg-suncoast-bg/30 p-4 rounded-lg border border-suncoast-bg">
                   <div className="flex justify-between text-sm mb-1 font-bold text-suncoast-secondary">
                     <span>{t.results.totalSurplus}</span>
                     <span>{formatCurrency(surplus)}</span>
                   </div>
                   <div className="w-full bg-gray-200 h-4 rounded-full overflow-hidden">
                      <div className="bg-suncoast-secondary h-full transition-all duration-500" style={{ width: `${surplusPercent}%` }}></div>
                   </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 transform scale-105 shadow-md">
                   <div className="flex justify-between text-sm mb-1 font-bold text-suncoast-accent">
                     <span>{t.results.providerShare}</span>
                     <span>{formatCurrency(providerShare)}</span>
                   </div>
                   <div className="w-full bg-gray-200 h-4 rounded-full overflow-hidden">
                      <div className="bg-suncoast-accent h-full transition-all duration-500" style={{ width: `${(providerShare / poolAmount) * 100}%` }}></div>
                   </div>
                </div>
             </div>
             
             <div className="mt-6 p-3 bg-gray-50 border border-gray-100 rounded text-center">
                <p className="text-[10px] text-gray-500 italic leading-tight">
                  {t.disclaimer}
                </p>
             </div>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-100 pt-4">
           <button 
             onClick={() => setShowExplanation(!showExplanation)}
             className="text-sm text-gray-500 hover:text-suncoast-primary flex items-center gap-1 font-medium mb-2"
           >
             {showExplanation ? '[-]' : '[+]'} {t.accordionTitle}
           </button>
           {showExplanation && (
             <p className="text-xs text-gray-500 mb-4 bg-gray-50 p-3 rounded">{t.accordionContent}</p>
           )}
        </div>
      </div>
    </div>
  );
}
